import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { itemIncidentApi, type ItemIncidentAnswerInput } from '@/lib/api';

/**
 * アイテム確認依頼の一覧を取得するフック。
 *
 * バイヤーがシートのステータスを書き換えたら早めに画面へ出したいので、
 * API 側のキャッシュ（2分）に合わせて staleTime も短くし、タブに戻ったときに取り直す。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business インシデント対応シートのアイテム確認依頼一覧を取得するフック
 * @graph-connects itemIncidentApi [calls] アイテム確認依頼APIクライアント呼び出し
 */
export function useItemIncidentList() {
  return useQuery({
    queryKey: ['item-incident', 'list'],
    queryFn: () => itemIncidentApi.getList(),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * 依頼1件の詳細（全文・回答・回答できるか）を取得するフック。行番号が無いときは取得しない。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼1件の詳細を取得するフック
 * @graph-connects itemIncidentApi [calls] アイテム確認依頼APIクライアント呼び出し
 */
export function useItemIncidentDetail(rowNumber: number | null) {
  return useQuery({
    queryKey: ['item-incident', 'detail', rowNumber],
    queryFn: () => itemIncidentApi.getDetail(rowNumber ?? 0),
    enabled: rowNumber !== null,
  });
}

/**
 * 回答を送信するフック。成功したら一覧と詳細を取り直す（ステータスの件数が変わるため）。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼に回答し、一覧と詳細を最新にするフック
 * @graph-connects itemIncidentApi [calls] アイテム確認依頼APIクライアント呼び出し
 */
export function useAnswerItemIncident(rowNumber: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ItemIncidentAnswerInput) => itemIncidentApi.answer(rowNumber, input),
    onSuccess: (result) => {
      queryClient.setQueryData(['item-incident', 'detail', rowNumber], result);
      void queryClient.invalidateQueries({ queryKey: ['item-incident', 'list'] });
    },
  });
}
